import React, { useState, useEffect } from 'react';
import { invoiceService } from '../services/invoice.service';
import { userService } from '../services/user.service';
import { useAuth } from '../contexts/AuthContext';
import { IInvoice, IUser, UserRole, IOrganizationInfo } from '../types';

const Invoices: React.FC = () => {
  const { user: authUser } = useAuth();
  const [invoices, setInvoices] = useState<IInvoice[]>([]);
  const [users, setUsers] = useState<IUser[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    userId: '',
    amount: '',
    currency: 'USD',
    description: '',
    dueDate: '',
    status: 'pending' as const
  });

  useEffect(() => {
    loadUsersAndInvoices();
  }, [authUser?.id]);

  const getVisibleUsers = (
    allUsers: IUser[],
    organization?: IOrganizationInfo
  ) => {
    if (!authUser) {
      return allUsers;
    }

    if (authUser.role === UserRole.SUPERADMIN) {
      return allUsers;
    }

    if (authUser.role === UserRole.ADMIN) {
      if (organization?.id) {
        return allUsers.filter(
          (user) => user.settings?.organization?.id === organization.id
        );
      }

      return allUsers.filter(
        (user) => user._id === authUser.id || user.email === authUser.email
      );
    }

    return allUsers;
  };

  const loadUsersAndInvoices = async () => {
    try {
      const usersResponse = await userService.getAllUsers();
      const allUsers = usersResponse.users as IUser[];
      const authUserRecord = authUser
        ? allUsers.find(
            (user) => user._id === authUser.id || user.email === authUser.email
          )
        : undefined;
      const organization = authUserRecord?.settings?.organization;
      const visibleUsers = getVisibleUsers(allUsers, organization);

      setUsers(visibleUsers);

      const invoiceResponse = await invoiceService.getAllInvoices();
      const allInvoices = invoiceResponse.invoices as IInvoice[];

      if (authUser?.role === UserRole.ADMIN) {
        const allowedUserIds = new Set(visibleUsers.map((user) => user._id));
        setInvoices(
          allInvoices.filter((invoice) => allowedUserIds.has(invoice.userId))
        );
      } else {
        setInvoices(allInvoices);
      }
    } catch (err) {
      console.error('Failed to load invoices or users', err);
    }
  };

  const handleCreateInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await invoiceService.createInvoice({
        ...formData,
        amount: parseFloat(formData.amount)
      });
      await loadUsersAndInvoices();
      setShowForm(false);
      resetForm();
    } catch (err) {
      console.error('Failed to create invoice', err);
    }
  };

  const resetForm = () => {
    setFormData({
      userId: '',
      amount: '',
      currency: 'USD',
      description: '',
      dueDate: '',
      status: 'pending'
    });
  };

  const getUserName = (userId: string) => {
    const user = users.find(u => u._id === userId);
    return user ? `${user.firstName} ${user.lastName}` : 'Unknown';
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem' }}>
        <h2>Invoice Management</h2>
        <button onClick={() => setShowForm(true)} style={buttonStyle}>Create Invoice</button>
      </div>

      {showForm && (
        <div style={modalStyle}>
          <div style={modalContentStyle}>
            <h3>Create New Invoice</h3>
            <form onSubmit={handleCreateInvoice} style={formStyle}>
              <select
                value={formData.userId}
                onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                required
                style={inputStyle}
              >
                <option value="">Select User</option>
                {users.map(user => (
                  <option key={user._id} value={user._id}>
                    {user.firstName} {user.lastName} ({user.email})
                  </option>
                ))}
              </select>
              <input
                type="number"
                placeholder="Amount"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                step="0.01"
                style={inputStyle}
              />
              <input
                type="text"
                placeholder="Currency"
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                required
                style={inputStyle}
              />
              <textarea
                placeholder="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                style={{ ...inputStyle, minHeight: '100px' }}
              />
              <input
                type="date"
                placeholder="Due Date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                required
                style={inputStyle}
              />
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" style={buttonStyle}>Create</button>
                <button type="button" onClick={() => setShowForm(false)} style={{ ...buttonStyle, background: '#95a5a6' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Invoice #</th>
            <th style={thStyle}>User</th>
            <th style={thStyle}>Amount</th>
            <th style={thStyle}>Description</th>
            <th style={thStyle}>Due Date</th>
            <th style={thStyle}>Status</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice) => (
            <tr key={invoice._id}>
              <td style={tdStyle}>{invoice.invoiceNumber}</td>
              <td style={tdStyle}>{getUserName(invoice.userId)}</td>
              <td style={tdStyle}>{invoice.amount} {invoice.currency}</td>
              <td style={tdStyle}>{invoice.description}</td>
              <td style={tdStyle}>{new Date(invoice.dueDate).toLocaleDateString()}</td>
              <td style={tdStyle}>
                <span style={getStatusStyle(invoice.status)}>{invoice.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const getStatusStyle = (status: string): React.CSSProperties => {
  const baseStyle: React.CSSProperties = {
    padding: '0.25rem 0.75rem',
    borderRadius: '12px',
    fontSize: '0.85rem',
    fontWeight: 'bold'
  };

  switch (status) {
    case 'paid':
      return { ...baseStyle, background: '#d4edda', color: '#155724' };
    case 'pending':
      return { ...baseStyle, background: '#fff3cd', color: '#856404' };
    case 'overdue':
      return { ...baseStyle, background: '#f8d7da', color: '#721c24' };
    default:
      return { ...baseStyle, background: '#e2e3e5', color: '#383d41' };
  }
};

const buttonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  background: '#3498db',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer'
};

const modalStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000
};

const modalContentStyle: React.CSSProperties = {
  background: 'white',
  padding: '2rem',
  borderRadius: '8px',
  minWidth: '500px',
  maxHeight: '90vh',
  overflow: 'auto'
};

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem'
};

const inputStyle: React.CSSProperties = {
  padding: '0.75rem',
  border: '1px solid #ddd',
  borderRadius: '4px',
  fontSize: '1rem'
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  background: 'white',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
};

const thStyle: React.CSSProperties = {
  padding: '1rem',
  textAlign: 'left',
  background: '#2c3e50',
  color: 'white',
  fontWeight: 'bold'
};

const tdStyle: React.CSSProperties = {
  padding: '0.75rem',
  borderBottom: '1px solid #ecf0f1'
};

export default Invoices;
